import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Plus, Trash2, Pencil, Search, X, Copy, FolderOpen } from "lucide-react";

const errText = (d, f) => (typeof d === "string" ? d : f);

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "" }); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, email: c.email, password: "" }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/clients/${editing.id}`, { name: form.name, password: form.password || undefined });
        toast.success("Client updated");
      } else {
        await api.post("/admin/clients", form);
        toast.success("Client created");
      }
      setShowForm(false); load();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, "Error saving client"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this client? Their projects will be unassigned.")) return;
    await api.delete(`/admin/clients/${id}`);
    toast.success("Client deleted"); load();
  };

  const filtered = clients.filter((c) =>
    (c.name + " " + c.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl">Clients</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm text-white focus:border-gold outline-none" />
          </div>
          <button data-testid="new-client-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-white transition-colors"><Plus size={15} /> New Client</button>
        </div>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`client-row-${c.id}`} className="flex flex-wrap items-center justify-between gap-4 p-5 hover:bg-white/[0.02]">
            <div>
              <p className="font-serif text-xl">{c.name}</p>
              <p className="text-white/40 text-sm">{c.email}</p>
            </div>
            <div className="flex items-center gap-5">
              <span className="text-xs text-white/50 flex items-center gap-2"><FolderOpen size={14} className="text-gold" /> {c.projects} project(s)</span>
              <button onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`} className="text-white/50 hover:text-white"><Pencil size={16} /></button>
              <button onClick={() => del(c.id)} data-testid={`delete-client-${c.id}`} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {!filtered.length && <p className="text-white/40 font-body p-6">No clients found.</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl">{editing ? "Edit Client" : "New Client"}</h3>
              <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-5" data-testid="client-form">
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className="editorial-input" data-testid="client-name" />
              <input required type="email" disabled={!!editing} placeholder="Email" value={form.email} onChange={set("email")} className="editorial-input disabled:opacity-50" data-testid="client-email" />
              <input type="text" required={!editing} placeholder={editing ? "New password (leave blank to keep)" : "Access password"} value={form.password} onChange={set("password")} className="editorial-input" data-testid="client-password" />
              <p className="text-white/40 text-xs">Share this email + password with your client so they can sign in at the Client Portal.</p>
              <button className="w-full bg-gold text-black text-xs uppercase tracking-widest py-3 hover:bg-white transition-colors" data-testid="client-save">{editing ? "Save changes" : "Create client"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
